/**
 * Trends "Highlights" tab — the 7.5-year archive made visible. Four panels:
 *  1. All-time records grid (max/min/tracked-days per headline metric).
 *  2. A GitHub-style calendar heatmap (last 12 months) for a picked metric.
 *  3. A monthly fitness composite (z-blend of VO₂ max, recovery, resting HR).
 *  4. A hearing story (headphone dB daily line vs the WHO 70 dB guideline).
 *
 * Charts stay inside the app's tree-shaken ECharts system (SleepView's
 * useEChart lifecycle, mode-aware chrome, palette-slot colours). The heatmap
 * ramp is a single hue: a light tint of the metric's own slot up to its
 * saturated slot colour — readable in both themes, no colour flood.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { init, use, type ECharts, type ComposeOption } from 'echarts/core';
import { LineChart, HeatmapChart, type LineSeriesOption, type HeatmapSeriesOption } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
  VisualMapComponent,
  CalendarComponent,
  type GridComponentOption,
  type TooltipComponentOption,
  type LegendComponentOption,
  type VisualMapComponentOption,
  type CalendarComponentOption,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { getMetricRecords, getMetrics, type MetricPoint, type MetricRecord } from '../api.js';
import { metricMeta, slotColor } from '../metrics.js';
import { useThemeMode } from '../lib/useThemeMode.js';
import { useUnitPref } from '../hooks/useUnitPref.js';
import { formatMetric, displayUnit } from '../lib/units.js';
import { fmtNumber, fmtYmd } from '../lib/format.js';
import { lastNDays } from '../lib/range.js';
import { Group, Row } from '../ui/index.js';
import { fitnessComposite, countLoudDays, type CompositePoint } from '../lib/highlights.js';

use([
  LineChart,
  HeatmapChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
  VisualMapComponent,
  CalendarComponent,
  CanvasRenderer,
]);

type ChartOption = ComposeOption<
  | LineSeriesOption
  | HeatmapSeriesOption
  | GridComponentOption
  | TooltipComponentOption
  | LegendComponentOption
  | VisualMapComponentOption
  | CalendarComponentOption
>;

function chrome(mode: 'light' | 'dark') {
  return mode === 'dark'
    ? { text: '#ffffff', muted: '#98989f', grid: '#38383a', axis: '#48484a', surface: '#1c1c1e', bg: '#000000' }
    : { text: '#000000', muted: '#8a8a8e', grid: '#e5e5ea', axis: '#c7c7cc', surface: '#ffffff', bg: '#f2f2f7' };
}

/** Blend two #rrggbb colours, t=0 → a, t=1 → b. */
function mixHex(a: string, b: string, t: number): string {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  const mixed = pa.map((v, i) => Math.round(v + (pb[i]! - v) * t));
  return '#' + mixed.map((v) => v.toString(16).padStart(2, '0')).join('');
}

/** Local calendar day (YYYY-MM-DD) of an ISO instant, in the viewer's frame. */
function localDay(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function useEChart(option: ChartOption | null): (el: HTMLDivElement | null) => void {
  const chartRef = useRef<ECharts | null>(null);
  const elRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = elRef.current;
    if (!el || !option) return;
    if (!chartRef.current || chartRef.current.isDisposed() || chartRef.current.getDom() !== el) {
      chartRef.current?.dispose();
      chartRef.current = init(el);
    }
    chartRef.current.setOption(option, true);
    const ro = new ResizeObserver(() => chartRef.current?.resize());
    ro.observe(el);
    return () => ro.disconnect();
  }, [option]);

  useEffect(
    () => () => {
      chartRef.current?.dispose();
      chartRef.current = null;
    },
    [],
  );

  return (el: HTMLDivElement | null) => {
    elRef.current = el;
  };
}

// --- Records ---------------------------------------------------------------

const RECORD_TYPES = [
  'steps',
  'distance_m',
  'flights_climbed',
  'resting_hr',
  'hrv_sdnn',
  'vo2max',
  'weight_kg',
] as const;

/** Metrics whose all-time BEST is the lowest value (everything else: the max). */
const RECORD_MIN_TYPES = new Set(['resting_hr', 'weight_kg']);

// --- Heatmap ---------------------------------------------------------------

const HEATMAP_TYPES = ['steps', 'active_calories', 'exercise_minutes', 'distance_m', 'flights_climbed'] as const;

function heatmapOption(
  points: MetricPoint[],
  type: string,
  pref: ReturnType<typeof useUnitPref>,
  mode: 'light' | 'dark',
): ChartOption {
  const c = chrome(mode);
  const cells = points.map(
    // Key by the bucket's UTC day (day-caggs bucket in UTC); viewer-local
    // conversion would shift a cell to the wrong calendar square.
    (p) => [p.t.slice(0, 10), Math.round(formatMetric(type, p.v, pref).value * 100) / 100] as [string, number],
  );
  const values = cells.map((cell) => cell[1]);
  const max = values.length ? Math.max(...values) : 1;
  const min = values.length ? Math.min(...values) : 0;
  const slot = slotColor(metricMeta(type).slot, mode);
  const tint = mixHex(c.bg, slot, 0.18);

  const end = new Date();
  const start = new Date(end.getTime());
  start.setMonth(start.getMonth() - 12);

  return {
    tooltip: {
      backgroundColor: mode === 'dark' ? '#2c2c2e' : '#ffffff',
      borderColor: 'transparent',
      borderWidth: 0,
      extraCssText: 'border-radius:12px;box-shadow:0 6px 24px rgba(0,0,0,.16);',
      textStyle: { color: c.text, fontSize: 13 },
      formatter: (p: unknown) => {
        const d = (p as { data: [string, number] }).data;
        const unit = displayUnit(type, pref);
        return `${fmtYmd(d[0])} · ${fmtNumber(d[1], 0)}${unit ? ` ${unit}` : ''}`;
      },
    },
    visualMap: {
      min,
      max,
      calculable: false,
      orient: 'horizontal',
      left: 'center',
      bottom: 2,
      itemWidth: 12,
      itemHeight: 90,
      inRange: { color: [tint, slot] },
      textStyle: { color: c.muted, fontSize: 13 },
    },
    calendar: {
      range: [localDay(start.toISOString()), localDay(end.toISOString())],
      cellSize: ['auto', 13],
      top: 24,
      left: 32,
      right: 12,
      itemStyle: { color: c.surface, borderColor: c.grid, borderWidth: 1 },
      yearLabel: { show: false },
      monthLabel: { color: c.muted, fontSize: 13 },
      dayLabel: { color: c.muted, fontSize: 13, firstDay: 1 },
      splitLine: { show: false },
    },
    series: [
      {
        type: 'heatmap',
        coordinateSystem: 'calendar',
        data: cells,
      },
    ],
  };
}

// --- Fitness composite -----------------------------------------------------

const COMPOSITE_KEYS: Record<string, { label: string; slot: number }> = {
  fitness: { label: 'Fitness', slot: 4 },
  vo2max: { label: 'VO₂ Max', slot: 4 },
  cardio_recovery_bpm: { label: 'Recovery', slot: 2 },
  resting_hr: { label: 'Resting HR', slot: 6 },
};

function compositeOption(rows: CompositePoint[], mode: 'light' | 'dark'): ChartOption {
  const c = chrome(mode);
  const months = rows.map((r) => r.month);
  const componentKeys = ['vo2max', 'cardio_recovery_bpm', 'resting_hr'];

  const faint: LineSeriesOption[] = componentKeys.map((key) => ({
    name: COMPOSITE_KEYS[key]!.label,
    type: 'line',
    smooth: true,
    showSymbol: false,
    connectNulls: false,
    lineStyle: { width: 1, color: slotColor(COMPOSITE_KEYS[key]!.slot, mode), opacity: 0.32 },
    itemStyle: { color: slotColor(COMPOSITE_KEYS[key]!.slot, mode), opacity: 0.32 },
    data: rows.map((r) => r.components[key] ?? null),
  }));

  const composite: LineSeriesOption = {
    name: 'Fitness',
    type: 'line',
    smooth: true,
    showSymbol: false,
    connectNulls: false,
    lineStyle: { width: 2.5, color: slotColor(COMPOSITE_KEYS.fitness!.slot, mode) },
    itemStyle: { color: slotColor(COMPOSITE_KEYS.fitness!.slot, mode) },
    data: rows.map((r) => (r.composite === null ? null : Math.round(r.composite * 100) / 100)),
  };

  return {
    grid: { left: 8, right: 12, top: 30, bottom: 24, containLabel: true },
    legend: {
      top: 2,
      left: 0,
      textStyle: { color: c.text, fontSize: 13 },
      icon: 'roundRect',
      itemWidth: 12,
      itemHeight: 3,
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: mode === 'dark' ? '#2c2c2e' : '#ffffff',
      borderColor: 'transparent',
      borderWidth: 0,
      extraCssText: 'border-radius:12px;box-shadow:0 6px 24px rgba(0,0,0,.16);',
      textStyle: { color: c.text, fontSize: 13 },
      valueFormatter: (v) => (typeof v === 'number' ? `${v > 0 ? '+' : ''}${v.toFixed(2)} z` : '—'),
    },
    xAxis: {
      type: 'category',
      data: months,
      axisLine: { lineStyle: { color: c.axis } },
      axisLabel: { color: c.muted, fontSize: 13, hideOverlap: true },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      name: 'z vs 1y',
      nameTextStyle: { color: c.muted, fontSize: 13, align: 'left' },
      axisLabel: { color: c.muted, fontSize: 13 },
      splitLine: { lineStyle: { color: c.grid } },
    },
    series: [...faint, composite],
  };
}

// --- Hearing ---------------------------------------------------------------

function hearingOption(points: MetricPoint[], mode: 'light' | 'dark'): ChartOption {
  const c = chrome(mode);
  const slot = slotColor(metricMeta('headphone_audio_db').slot, mode);
  return {
    grid: { left: 8, right: 12, top: 20, bottom: 24, containLabel: true },
    tooltip: {
      trigger: 'axis',
      backgroundColor: mode === 'dark' ? '#2c2c2e' : '#ffffff',
      borderColor: 'transparent',
      borderWidth: 0,
      extraCssText: 'border-radius:12px;box-shadow:0 6px 24px rgba(0,0,0,.16);',
      textStyle: { color: c.text, fontSize: 13 },
      valueFormatter: (v) => (typeof v === 'number' ? `${v.toFixed(0)} dB` : '—'),
    },
    xAxis: {
      type: 'time',
      axisLine: { lineStyle: { color: c.axis } },
      axisLabel: { color: c.muted, fontSize: 13, hideOverlap: true },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      name: 'dB',
      scale: true,
      nameTextStyle: { color: c.muted, fontSize: 13, align: 'left' },
      axisLabel: { color: c.muted, fontSize: 13 },
      splitLine: { lineStyle: { color: c.grid } },
    },
    series: [
      {
        type: 'line',
        smooth: true,
        showSymbol: false,
        lineStyle: { width: 1.5, color: slot },
        itemStyle: { color: slot },
        data: points.map((p) => [p.t, Math.round(p.v * 10) / 10] as [string, number]),
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: c.muted, type: 'dashed', width: 1 },
          label: { color: c.muted, fontSize: 13, formatter: 'WHO 70 dB' },
          data: [{ yAxis: 70 }],
        },
      },
    ],
  };
}

// --- Component -------------------------------------------------------------

type Load<T> = { status: 'loading' } | { status: 'error' } | { status: 'ready'; data: T };

export default function HighlightsView(): JSX.Element {
  const mode = useThemeMode();
  const pref = useUnitPref();
  const [records, setRecords] = useState<Load<Record<string, MetricRecord | null>>>({ status: 'loading' });
  const [heatMetric, setHeatMetric] = useState<string>('steps');
  const [heatPoints, setHeatPoints] = useState<MetricPoint[] | null>(null);
  const [composite, setComposite] = useState<CompositePoint[] | null>(null);
  const [hearing, setHearing] = useState<MetricPoint[] | null>(null);

  useEffect(() => {
    let alive = true;
    getMetricRecords([...RECORD_TYPES])
      .then((r) => alive && setRecords({ status: 'ready', data: r }))
      .catch(() => alive && setRecords({ status: 'error' }));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    setHeatPoints(null);
    const { from, to } = lastNDays(365);
    getMetrics({ type: heatMetric, from, to, bucket: 'day', agg: metricMeta(heatMetric).agg })
      .then((r) => alive && setHeatPoints(r.points))
      .catch(() => alive && setHeatPoints([]));
    return () => {
      alive = false;
    };
  }, [heatMetric]);

  useEffect(() => {
    let alive = true;
    const { from, to } = lastNDays(365);
    Promise.all(
      ['vo2max', 'cardio_recovery_bpm', 'resting_hr'].map((type) =>
        getMetrics({ type, from, to, bucket: 'day', agg: 'avg' }).then((r) => r.points),
      ),
    )
      .then(([vo2, recovery, rhr]) => {
        if (!alive) return;
        setComposite(
          fitnessComposite([
            { key: 'vo2max', points: vo2!, invert: false },
            // Higher 1-min HR recovery is fitter → not inverted.
            { key: 'cardio_recovery_bpm', points: recovery!, invert: false },
            { key: 'resting_hr', points: rhr!, invert: true },
          ]),
        );
      })
      .catch(() => alive && setComposite([]));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    const { from, to } = lastNDays(365);
    getMetrics({ type: 'headphone_audio_db', from, to, bucket: 'day', agg: 'avg' })
      .then((r) => alive && setHearing(r.points))
      .catch(() => alive && setHearing([]));
    return () => {
      alive = false;
    };
  }, []);

  const heatOpt = useMemo(
    () => (heatPoints && heatPoints.length > 0 ? heatmapOption(heatPoints, heatMetric, pref, mode) : null),
    [heatPoints, heatMetric, pref, mode],
  );
  const compOpt = useMemo(
    () => (composite && composite.length > 0 ? compositeOption(composite, mode) : null),
    [composite, mode],
  );
  const hearOpt = useMemo(
    () => (hearing && hearing.length > 0 ? hearingOption(hearing, mode) : null),
    [hearing, mode],
  );
  const heatRef = useEChart(heatOpt);
  const compRef = useEChart(compOpt);
  const hearRef = useEChart(hearOpt);

  // Loud-listening days in the trailing 90 days (points are day-bucketed daily).
  const loudDays = useMemo(() => {
    if (!hearing) return null;
    const cutoff = Date.now() - 90 * 86_400_000;
    return countLoudDays(hearing.filter((p) => new Date(p.t).getTime() >= cutoff));
  }, [hearing]);

  return (
    <section className="highlights hb-highlights">
      {records.status === 'error' ? (
        <div className="state state--error" style={{ marginBottom: 24 }}>
          <div className="state__title">Couldn't load records</div>
        </div>
      ) : (
        <Group header="All-time bests">
          {RECORD_TYPES.map((type) => {
            const meta = metricMeta(type);
            if (records.status === 'loading') return <li key={type} className="hb-skelrow" aria-hidden="true" />;
            const rec = records.data[type] ?? null;
            const unit = displayUnit(type, pref);
            // For resting HR and weight the achievement is the lowest reading.
            const best = rec ? (RECORD_MIN_TYPES.has(type) ? rec.min : rec.max) : null;
            if (!rec || !best) return null;
            return (
              <Row
                key={type}
                leading={<span className="ui-dot hb-dot--lg" style={{ background: slotColor(meta.slot, mode) }} aria-hidden="true" />}
                title={meta.label}
                sub={`${fmtYmd(best.date)} · ${fmtNumber(rec.total, 0)} days tracked`}
                detail={
                  <span className="hb-best">
                    {fmtNumber(formatMetric(type, best.v, pref).value, 0)}
                    {unit && <small> {unit}</small>}
                  </span>
                }
              />
            );
          })}
        </Group>
      )}

      <section className="hb-chart" aria-label="Calendar">
        <div className="hb-chart__head">
          <div>
            <h2 className="hb-chart__title">Last 12 months</h2>
            <span className="hb-chart__sub">{metricMeta(heatMetric).label} per day</span>
          </div>
        </div>
        <div className="hb-chiprow hb-chiprow--inset" role="tablist" aria-label="Calendar metric">
          {HEATMAP_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={heatMetric === t}
              className={`ui-chip${heatMetric === t ? ' ui-chip--on' : ''}`}
              onClick={() => setHeatMetric(t)}
            >
              {metricMeta(t).label}
            </button>
          ))}
        </div>
        {heatPoints === null ? (
          <div className="skeleton" style={{ height: 180 }} />
        ) : heatPoints.length === 0 ? (
          <p className="hb-hint hb-chart__empty">No {metricMeta(heatMetric).label.toLowerCase()} in the last year.</p>
        ) : (
          <div className="hb-heatwrap">
            <div ref={heatRef} className="heatmap-chart" role="img" aria-label="Calendar heatmap" />
          </div>
        )}
      </section>

      {(composite === null || composite.length > 0) && (
        <section className="hb-chart" aria-label="Fitness composite">
          <div className="hb-chart__head">
            <div>
              <h2 className="hb-chart__title">Fitness</h2>
              <span className="hb-chart__sub">VO₂ max, recovery &amp; resting HR vs your 1-year norm</span>
            </div>
          </div>
          {composite === null ? (
            <div className="skeleton" style={{ height: 280 }} />
          ) : (
            <div ref={compRef} className="highlights-chart" role="img" aria-label="Fitness composite" />
          )}
        </section>
      )}

      {(hearing === null || hearing.length > 0) && (
        <section className="hb-chart" aria-label="Hearing">
          <div className="hb-chart__head">
            <div>
              <h2 className="hb-chart__title">Headphone level</h2>
              {loudDays !== null && (
                <span className="hb-chart__sub">
                  {loudDays} loud day{loudDays === 1 ? '' : 's'} (&gt;70 dB) in 90 days
                </span>
              )}
            </div>
          </div>
          {hearing === null ? (
            <div className="skeleton" style={{ height: 260 }} />
          ) : (
            <div ref={hearRef} className="highlights-chart" role="img" aria-label="Headphone audio level" />
          )}
        </section>
      )}
    </section>
  );
}
